/**
 * Tilly Design Tokens
 *
 * Design Direction: Sage / Botanical Luxury
 * Think: Clean, modern CRM with natural light, plants, linen, stone
 */

// =============================================================================
// Colors
// =============================================================================

export const colors = {
  // Backgrounds
  bg: '#F6F7F5', // Warm off-white / linen
  surface: '#FFFFFF', // Pure white for cards
  surfaceRaised: '#F1F3F0', // Subtle raised surface
  border: '#DADFD8', // Soft sage-tinted dividers

  // Text
  textPrimary: '#1F2933', // Charcoal (not black)
  textSecondary: '#5F6B66', // Medium gray-green
  textMuted: '#8A948E', // Muted helper text

  // Primary Accent — Sage
  accent: '#7FA897', // Sage 500 (primary)
  accentHover: '#6E9686', // Sage 600 (hover/active)
  accentActive: '#5E8675', // Sage 700 (pressed)
  accentLight: '#E6F0EB', // Sage 200 (backgrounds)
  accentLighter: '#F1F7F4', // Sage 100 (very subtle)

  // Secondary Accent — Warm Brass (restrained)
  brass: '#C9A46B', // Primary brass for CTAs
  brassHover: '#B89156', // Brass hover
  brassLight: '#E6D4B3', // Soft brass tint

  // Semantic
  success: '#5FAF8E', // Sage-adjacent success
  warning: '#E3B341', // Warm warning
  danger: '#D86A6A', // Soft danger
  error: '#D86A6A', // Alias for danger

  // Additional utilities
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',
} as const;

// =============================================================================
// Typography
// =============================================================================

export const fonts = {
  heading: '"Fraunces", Georgia, serif',
  body: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
} as const;

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const typography = {
  h1: {
    fontFamily: fonts.heading,
    fontWeight: fontWeights.semibold,
    fontSize: '32px',
    lineHeight: '40px',
  },
  h2: {
    fontFamily: fonts.heading,
    fontWeight: fontWeights.semibold,
    fontSize: '24px',
    lineHeight: '32px',
  },
  h3: {
    fontFamily: fonts.body,
    fontWeight: fontWeights.semibold,
    fontSize: '18px',
    lineHeight: '26px',
  },
  body: {
    fontFamily: fonts.body,
    fontWeight: fontWeights.regular,
    fontSize: '14px',
    lineHeight: '22px',
  },
  bodyMedium: {
    fontFamily: fonts.body,
    fontWeight: fontWeights.medium,
    fontSize: '14px',
    lineHeight: '22px',
  },
  label: {
    fontFamily: fonts.body,
    fontWeight: fontWeights.semibold,
    fontSize: '12px',
    lineHeight: '16px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  small: {
    fontFamily: fonts.body,
    fontWeight: fontWeights.regular,
    fontSize: '12px',
    lineHeight: '16px',
  },
} as const;

// =============================================================================
// Spacing
// =============================================================================

export const spacing = {
  0: '0px',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
} as const;

// Common spacing values from spec
export const spacingValues = {
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
} as const;

// =============================================================================
// Border Radius
// =============================================================================

export const radius = {
  sm: '10px',
  md: '14px',
  lg: '18px',
  xl: '22px',
  full: '9999px',
} as const;

// =============================================================================
// Shadows (subtle only)
// =============================================================================

export const shadows = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
  // Prefer borders over shadows per spec
} as const;

// =============================================================================
// Transitions
// =============================================================================

export const transitions = {
  fast: '150ms ease',
  normal: '200ms ease',
  slow: '300ms ease',
} as const;

/**
 * Returns the appropriate transition string based on reduceMotion preference.
 * Usage: `transition: motionTransition(reduceMotion, transitions.normal)`
 */
export function motionTransition(reduceMotion: boolean, value: string): string {
  return reduceMotion ? 'none' : value;
}

// =============================================================================
// Density spacing multipliers
// Density 'compact' = tighter padding, 'spacious' = more breathing room.
// These are used by ADHD-friendly components to adjust padding based on the
// usePreferencesStore density value.
// =============================================================================

export const densitySpacing = {
  compact: {
    pagePadding: '16px 24px',
    sectionGap: '16px',
    cardPadding: '12px 16px',
    rowHeight: '44px',
  },
  comfortable: {
    pagePadding: '24px 32px',
    sectionGap: '24px',
    cardPadding: '20px 24px',
    rowHeight: '56px',
  },
  spacious: {
    pagePadding: '32px 48px',
    sectionGap: '32px',
    cardPadding: '28px 32px',
    rowHeight: '68px',
  },
} as const;

// =============================================================================
// Z-Index
// =============================================================================

export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  modal: 300,
  tooltip: 400,
  toast: 500,
} as const;
